import React, { useState } from "react";
import { Check, Copy as CopyIcon, LoaderCircle, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { createDropzoneLink, apiErrorMessage } from "../../../../service/apiService";
import {
  CreatedDropzoneLink,
  CreateDropzoneLinkRequest,
  DropzoneExtension,
  DropzoneExpiryMinutes,
  DropzoneProcessingMode,
} from "../../../../types";

/**
 * The inline "New upload link" panel on the View BrainBank screen (design G
 * on the canvas, form B). Opens in place under the BrainBank card; the
 * one-time URL replaces the form in the same panel. Never a second dialog:
 * the BrainBanks screen is already one.
 */

export const EXPIRY_CHOICES: Array<{ value: DropzoneExpiryMinutes; label: string }> = [
  { value: 60, label: "1 hour" },
  { value: 480, label: "8 hours" },
  { value: 1440, label: "24 hours" },
  { value: 2880, label: "48 hours" },
];
export const EXTENSION_CHOICES: DropzoneExtension[] = ["pdf", "docx", "xlsx", "txt"];
export const SIZE_CHOICES: Array<10 | 25 | 50> = [10, 25, 50];
export const MAX_FILES_LIMIT = 50;
export const LABEL_MAX_LENGTH = 80;
export const PASSCODE_MIN_LENGTH = 6;
export const PASSCODE_MAX_LENGTH = 32;
export const DEFAULT_EXPIRY: DropzoneExpiryMinutes = 480;
export const DEFAULT_MAX_FILES = 10;
// From here up, a plain link is open for a day or more: nudge, never block.
export const LONG_LINK_MINUTES = 1440;

// The same two modes, named and described exactly as on the Upload Files tab
// (UploadFiles.tsx processingOptions), so a link's files land where that
// screen's choice would send them and nobody meets a third name for it.
export const PROCESSING_OPTIONS: Array<{
  id: DropzoneProcessingMode;
  name: string;
  description: string;
  processingTime: string;
  recommended?: boolean;
}> = [
  {
    id: "standard",
    name: "Standard Processing",
    description: "Quick text extraction without AI enhancement.",
    processingTime: "Quick processing",
  },
  {
    id: "advanced",
    name: "Advanced Processing (AI Enhanced)",
    description: "AI-powered extraction with table analysis, image recognition, and deep content understanding.",
    processingTime: "Queued processing (takes longer)",
    recommended: true,
  },
];

export const processingModeLabel = (mode: DropzoneProcessingMode | undefined): string =>
  PROCESSING_OPTIONS.find((o) => o.id === (mode ?? "standard"))?.name ?? "Standard Processing";

/** One format for an expiry instant, shared with the links table. */
export const absoluteTime = (iso: string): string =>
  new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const expiryLabel = (minutes: number): string =>
  EXPIRY_CHOICES.find((c) => c.value === minutes)?.label ?? `${minutes} min`;

export const describeRules = (link: {
  allowed_extensions: DropzoneExtension[];
  max_file_bytes: number;
  max_files: number;
  processing_mode?: DropzoneProcessingMode;
  passcode_required?: boolean;
  hold_for_approval?: boolean;
}): string =>
  [
    link.allowed_extensions.map((e) => e.toUpperCase()).join(", "),
    `${Math.round(link.max_file_bytes / (1024 * 1024))} MB`,
    `${link.max_files} ${link.max_files === 1 ? "file" : "files"}`,
    processingModeLabel(link.processing_mode),
    ...(link.passcode_required ? ["Passcode"] : []),
    ...(link.hold_for_approval ? ["Approval"] : []),
  ].join(" · ");

interface Props {
  brainBank: { id: string; name: string };
  /** Called once the link exists so the list can refresh. */
  onCreated: (link: CreatedDropzoneLink) => void;
  /** Cancel, or Done after the URL was shown. */
  onClose: () => void;
}

const segment = (active: boolean) =>
  `h-9 rounded-lg text-sm font-medium transition-colors ${
    active ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
  }`;

const chip = (active: boolean) =>
  `h-8 px-3 rounded-full border text-sm font-medium transition-colors ${
    active
      ? "border-violet-600 bg-violet-50 text-violet-800"
      : "border-gray-300 bg-white text-gray-500 hover:border-gray-400"
  }`;

const Switch: React.FC<{
  on: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint: string;
}> = ({ on, onChange, label, hint }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 min-h-[44px]">
    <span className="flex flex-col">
      <span className="text-sm font-medium text-gray-800">{label}</span>
      <span className="text-xs text-gray-500">{hint}</span>
    </span>
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-[26px] w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 ${
        on ? "bg-violet-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          on ? "translate-x-[22px]" : "translate-x-[3px]"
        }`}
      />
    </button>
  </div>
);

const UploadLinkPanel: React.FC<Props> = ({ brainBank, onCreated, onClose }) => {
  const [label, setLabel] = useState("");
  const [expiresIn, setExpiresIn] = useState<DropzoneExpiryMinutes>(DEFAULT_EXPIRY);
  const [extensions, setExtensions] = useState<DropzoneExtension[]>([...EXTENSION_CHOICES]);
  const [maxFileMb, setMaxFileMb] = useState<10 | 25 | 50>(25);
  const [maxFiles, setMaxFiles] = useState<number>(DEFAULT_MAX_FILES);
  const [processingMode, setProcessingMode] = useState<DropzoneProcessingMode>("standard");
  const [passcodeOn, setPasscodeOn] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [holdForApproval, setHoldForApproval] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // The URL is shown once. It lives here only while the panel is open.
  const [created, setCreated] = useState<CreatedDropzoneLink | null>(null);
  const [copied, setCopied] = useState(false);

  const toggleExtension = (ext: DropzoneExtension) =>
    setExtensions((current) =>
      current.includes(ext) ? current.filter((e) => e !== ext) : [...current, ext]
    );

  const trimmedLabel = label.trim();
  const trimmedPasscode = passcode.trim();
  const maxFilesValid = Number.isInteger(maxFiles) && maxFiles >= 1 && maxFiles <= MAX_FILES_LIMIT;
  const passcodeValid =
    !passcodeOn ||
    (trimmedPasscode.length >= PASSCODE_MIN_LENGTH && trimmedPasscode.length <= PASSCODE_MAX_LENGTH);
  const canSubmit =
    trimmedLabel.length > 0 &&
    trimmedLabel.length <= LABEL_MAX_LENGTH &&
    extensions.length > 0 &&
    maxFilesValid &&
    passcodeValid &&
    !submitting;
  const longAndOpen = expiresIn >= LONG_LINK_MINUTES && !passcodeOn && !holdForApproval;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const body: CreateDropzoneLinkRequest = {
      knowledge_base_id: brainBank.id,
      label: trimmedLabel,
      expires_in_minutes: expiresIn,
      allowed_extensions: EXTENSION_CHOICES.filter((e) => extensions.includes(e)),
      processing_mode: processingMode,
      max_file_mb: maxFileMb,
      max_files: maxFiles,
      hold_for_approval: holdForApproval,
      ...(passcodeOn && trimmedPasscode ? { passcode: trimmedPasscode } : {}),
    };
    setSubmitting(true);
    try {
      const link = await createDropzoneLink(body);
      setCreated(link);
      onCreated(link);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not create the upload link"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1000);
    } catch {
      toast.error("Copy failed, select the link and copy it");
    }
  };

  if (created) {
    return (
      <section
        aria-label="Upload link ready"
        className="rounded-xl border border-violet-200 bg-violet-50 p-5 space-y-4"
      >
        <div className="flex items-center gap-2">
          <Check className="h-5 w-5 text-violet-700" />
          <h4 className="text-sm font-semibold text-violet-950">
            {created.url_available ? "Link ready" : "Link ready. Copy it now, it is shown once."}
          </h4>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={created.url}
            aria-label="Upload link"
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-800 font-mono"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex h-11 items-center gap-1 rounded-lg bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            {copied ? <Check size={16} /> : <CopyIcon size={16} />}
            Copy link
          </button>
        </div>
        <p className="text-sm text-gray-700">
          Expires {absoluteTime(created.expires_at)}
          <span className="text-gray-400"> · </span>
          {describeRules(created)}
        </p>
        {created.url_available && (
          <p className="text-sm text-gray-700">
            You can copy this link again from the list below while it is active.
          </p>
        )}
        {created.passcode_required && (
          <p className="text-sm text-gray-700">
            Share the passcode separately, for example by phone or text. It is not in the link.
          </p>
        )}
        {created.hold_for_approval && (
          <p className="text-sm text-gray-700">
            Files will wait here for your approval before they are processed.
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 items-center rounded-lg bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            Done
          </button>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="New upload link" className="rounded-xl border border-violet-200 bg-violet-50/60 p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold text-gray-900">New upload link</h4>
        <span className="text-xs text-gray-500">Uploads go to {brainBank.name}. Your client needs no Kyva account.</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <div className="space-y-1.5">
          <label htmlFor="dropzone-label" className="block text-sm font-medium text-gray-700">
            Label
          </label>
          <input
            id="dropzone-label"
            type="text"
            value={label}
            maxLength={LABEL_MAX_LENGTH}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Q3 contracts"
            autoFocus
            className="w-full h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-violet-500 focus:ring-violet-500"
          />
        </div>

        <div className="space-y-1.5">
          <span className="block text-sm font-medium text-gray-700">Expires in</span>
          <div role="group" aria-label="Expires in" className="grid grid-cols-4 gap-1 rounded-lg bg-gray-200 p-[3px]">
            {EXPIRY_CHOICES.map((choice) => (
              <button
                key={choice.value}
                type="button"
                aria-pressed={expiresIn === choice.value}
                onClick={() => setExpiresIn(choice.value)}
                className={segment(expiresIn === choice.value)}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <span className="block text-sm font-medium text-gray-700">File types</span>
          <div role="group" aria-label="File types" className="flex flex-wrap gap-1.5">
            {EXTENSION_CHOICES.map((ext) => (
              <button
                key={ext}
                type="button"
                aria-pressed={extensions.includes(ext)}
                onClick={() => toggleExtension(ext)}
                className={chip(extensions.includes(ext))}
              >
                {ext.toUpperCase()}
              </button>
            ))}
          </div>
          {extensions.length === 0 && <p className="text-xs text-red-600">Pick at least one file type.</p>}
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 items-end">
          <div className="space-y-1.5">
            <span className="block text-sm font-medium text-gray-700">Max file size</span>
            <div role="group" aria-label="Max file size" className="grid grid-cols-3 gap-1 rounded-lg bg-gray-200 p-[3px]">
              {SIZE_CHOICES.map((mb) => (
                <button
                  key={mb}
                  type="button"
                  aria-pressed={maxFileMb === mb}
                  onClick={() => setMaxFileMb(mb)}
                  className={`${segment(maxFileMb === mb)} h-8`}
                >
                  {mb} MB
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="dropzone-max-files" className="block text-sm font-medium text-gray-700">
              Max files
            </label>
            <div className="flex items-center overflow-hidden rounded-lg border border-gray-300 bg-white">
              <button
                type="button"
                aria-label="Fewer files"
                onClick={() => setMaxFiles((n) => Math.max(1, n - 1))}
                className="h-9 w-9 flex items-center justify-center bg-gray-50 text-gray-700 hover:bg-gray-100"
              >
                <Minus size={14} />
              </button>
              <input
                id="dropzone-max-files"
                type="number"
                min={1}
                max={MAX_FILES_LIMIT}
                value={maxFiles}
                onChange={(e) => setMaxFiles(Number(e.target.value))}
                className="h-9 w-14 border-0 border-x border-gray-200 text-center text-sm focus:ring-0"
              />
              <button
                type="button"
                aria-label="More files"
                onClick={() => setMaxFiles((n) => Math.min(MAX_FILES_LIMIT, n + 1))}
                className="h-9 w-9 flex items-center justify-center bg-gray-50 text-gray-700 hover:bg-gray-100"
              >
                <Plus size={14} />
              </button>
            </div>
            {!maxFilesValid && <p className="text-xs text-red-600">1 to {MAX_FILES_LIMIT}.</p>}
          </div>
        </div>
      </div>

      <div className="space-y-1.5 border-t border-violet-200 pt-4">
        <span className="block text-sm font-medium text-gray-700">Processing mode for files</span>
        <div role="group" aria-label="Processing mode for files" className="grid grid-cols-1 sm:grid-cols-2 gap-1 rounded-lg bg-gray-200 p-[3px]">
          {PROCESSING_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={processingMode === option.id}
              onClick={() => setProcessingMode(option.id)}
              className={segment(processingMode === option.id)}
            >
              {option.name}
              {option.recommended && (
                <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                  Recommended
                </span>
              )}
            </button>
          ))}
        </div>
        {PROCESSING_OPTIONS.filter((o) => o.id === processingMode).map((option) => (
          <p key={option.id} className="text-xs text-gray-500">
            {option.description} Processing time: {option.processingTime}
          </p>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Switch
          on={passcodeOn}
          onChange={(next) => {
            setPasscodeOn(next);
            if (!next) setPasscode("");
          }}
          label="Require a passcode"
          hint="You share it by phone or text, not with the link."
        />
        <Switch
          on={holdForApproval}
          onChange={setHoldForApproval}
          label="Hold for my approval"
          hint="Nothing is processed until you approve it here."
        />
      </div>

      {passcodeOn && (
        <div className="sm:w-1/2 space-y-1.5">
          <label htmlFor="dropzone-passcode" className="block text-sm font-medium text-gray-700">
            Passcode
          </label>
          <input
            id="dropzone-passcode"
            type="text"
            autoComplete="off"
            value={passcode}
            maxLength={PASSCODE_MAX_LENGTH}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder={`${PASSCODE_MIN_LENGTH} to ${PASSCODE_MAX_LENGTH} characters`}
            className="w-full h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-violet-500 focus:ring-violet-500"
          />
          {!passcodeValid && (
            <p className="text-xs text-red-600">
              {PASSCODE_MIN_LENGTH} to {PASSCODE_MAX_LENGTH} characters.
            </p>
          )}
        </div>
      )}

      {longAndOpen && (
        <p role="note" className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          A {expiryLabel(expiresIn)} link stays open that whole time for anyone who has it. Consider a passcode or holding files for approval.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500">The link appears here when it is ready.</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 items-center rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="inline-flex h-11 items-center gap-2 rounded-lg bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting && <LoaderCircle size={16} className="animate-spin" />}
            Generate link
          </button>
        </div>
      </div>
    </section>
  );
};

export default UploadLinkPanel;
